export type AggregatorOption = string; // Dynamic gateway names from API

export type SettlementDate = 'T' | 'T+1' | 'T+2';

export const SETTLEMENT_DATE_OPTIONS: SettlementDate[] = ['T', 'T+1', 'T+2'];

export const DEFAULT_SETTLEMENT_DATE: SettlementDate = 'T+1';

export interface NetbankingInfo {
  bankId: string;
  hybridBankId?: string;
  lumpsumDebitCardBankId?: string;
  bankIdCorporate?: string;
  hybridBankIdCorporate?: string;
  lumpsumDebitCardBankIdCorporate?: string;
  lumpsum: boolean;
  hybrid: boolean;
  lumpsumDebitCard: boolean;
  lumpsumSettlement?: SettlementDate;
  hybridSettlement?: SettlementDate;
  lumpsumDebitCardSettlement?: SettlementDate;
}

export interface EmandateInfo {
  bankId: string;
  bankIdCorporate?: string;
  netBanking: boolean;
  debitCard: boolean;
  aadhaar: boolean;
  pan: boolean;
  netBankingSettlement?: SettlementDate;
  debitCardSettlement?: SettlementDate;
  aadhaarSettlement?: SettlementDate;
  panSettlement?: SettlementDate;
}

export interface BlacklistEntry {
  id?: number | string;
  merchantId: string;
  gateway: string;
  bankName: string;
  ifscCode: string;
  paymode?: string;
  trtype?: string;
  isActive: boolean;
  remarks?: string;
  reason?: string;
  performedBy?: string;
  updatedAt?: string;
}

export interface BankRow {
  id: number;
  name: string;
  ifscCode: string;
  netbanking: NetbankingInfo;
  emandate: EmandateInfo;
  blacklistedMerchants?: BlacklistEntry[];
  blacklistReason?: string;
  // Per-mode settlement days are only populated when the API was called with a
  // merchantId; otherwise these are all undefined and the UI hides the column.
  settlementDays?: {
    lumpsumNetBanking?: string;
    lumpsumDebitCard?: string;
    hybridNetBanking?: string;
    emandateNetBanking?: string;
    emandateDebitCard?: string;
    emandateAadhar?: string;
    emandatePAN?: string;
  };
}

// Settlement fields in the form may be the empty string when the source row
// has no value for that paymode. Submit handlers must omit empty values.
export type SettlementFormValue = SettlementDate | '';

export interface BankFormData {
  gateway: string;
  name: string;
  ifscCode: string;
  nbBankId: string;
  nbHybridBankId: string;
  nbDebitCardBankId: string;
  // Corporate (current-account) bank IDs entered alongside their regular
  // counterparts. Empty string means "leave unchanged" on edit.
  nbBankIdCorporate: string;
  nbHybridBankIdCorporate: string;
  nbDebitCardBankIdCorporate: string;
  nbLumpsum: boolean;
  nbHybrid: boolean;
  nbDebitCard: boolean;
  emdBankId: string;
  emdBankIdCorporate: string;
  emdNetBanking: boolean;
  emdDebitCard: boolean;
  emdAadhaar: boolean;
  emdPan: boolean;
  nbLumpsumSettlement: SettlementFormValue;
  nbHybridSettlement: SettlementFormValue;
  nbDebitCardSettlement: SettlementFormValue;
  emdNetBankingSettlement: SettlementFormValue;
  emdDebitCardSettlement: SettlementFormValue;
  emdAadhaarSettlement: SettlementFormValue;
  emdPanSettlement: SettlementFormValue;
  remarks: string;
}

export const getGatewayParam = (option: AggregatorOption): string => {
  // For dynamic gateways, use the gateway ID directly
  // The API should provide the correct ID that can be used in requests
  // Fallback to original mapping for backward compatibility
  const fallbackMapping: Record<string, string> = {
    'Billdesk V1': 'Billdesk1',
    'Billdesk V2': 'Billdesk2',
    'CAMS Pay': 'CamsPay',
    'Kotak': 'Kotak',
    'CashFree': 'CashFree',
  };
  
  return option || fallbackMapping[option] || option;
};

export const coerceBoolean = (value: any): boolean => {
  if (typeof value === 'boolean') return value;
  if (typeof value === 'number') return value !== 0;
  if (typeof value === 'string') return ['true', 'yes', 'y', '1'].includes(value.toLowerCase());
  return !!value;
};

export const coerceSettlementDate = (value: any): SettlementDate => {
  if (value === undefined || value === null) return DEFAULT_SETTLEMENT_DATE;
  const normalized = String(value).trim().toUpperCase().replace(/\s+/g, '');
  if (normalized === 'T' || normalized === 'T+0') return 'T';
  if (normalized === 'T+1') return 'T+1';
  if (normalized === 'T+2') return 'T+2';
  return DEFAULT_SETTLEMENT_DATE;
};

// Returns undefined when the value is missing/empty so settlement chips can be
// suppressed entirely (used when no merchantId is supplied to GET /bank-details).
export const coerceOptionalSettlementDate = (value: any): SettlementDate | undefined => {
  if (value === undefined || value === null || value === '') return undefined;
  const normalized = String(value).trim().toUpperCase().replace(/\s+/g, '');
  if (normalized === '') return undefined;
  if (normalized === 'T' || normalized === 'T+0') return 'T';
  if (normalized === 'T+1') return 'T+1';
  if (normalized === 'T+2') return 'T+2';
  return undefined;
};

export const mapToBankRow = (item: any, index: number): BankRow => ({
  id: item.id ?? index + 1,
  name: item.name ?? item.bankName ?? '-',
  ifscCode: item.ifscCode ?? item.ifsc ?? '-',
  netbanking: {
    bankId: item.lumpsumNetBankingBankId ?? '-',
    hybridBankId: item.hybridNetBankingBankId ?? '-',
    lumpsumDebitCardBankId: item.lumpsumDebitCardBankId ?? '-',
    bankIdCorporate: item.lumpsumNetBankingBankIdCorporate ?? undefined,
    hybridBankIdCorporate: item.hybridNetBankingBankIdCorporate ?? undefined,
    lumpsumDebitCardBankIdCorporate:
      item.lumpsumDebitCardBankIdCorporate ?? undefined,
    lumpsum: coerceBoolean(
      item.netbanking?.lumpsum ??
      item.nbLumpsum ??
      item.LumpsumNetBanking ??
      item.LumpsumNetbanking
    ),
    hybrid: coerceBoolean(
      item.netbanking?.hybrid ??
      item.nbHybrid ??
      item.HybridNetBanking ??
      item.HybridNetbanking
    ),
    lumpsumDebitCard: coerceBoolean(
      item.netbanking?.lumpsumDebitCard ??
      item.LumpsumDebitCard
    ),
    lumpsumSettlement: coerceOptionalSettlementDate(
      item.lumpsumNetBankingSettlementDays ??
      item.nbLumpsumSettlementDays ??
      item.netbanking?.lumpsumSettlement ??
      item.lumpsumNetBankingSettlementDate ??
      item.LumpsumNetBankingSettlementDate
    ),
    hybridSettlement: coerceOptionalSettlementDate(
      item.hybridNetBankingSettlementDays ??
      item.nbHybridSettlementDays ??
      item.netbanking?.hybridSettlement ??
      item.hybridNetBankingSettlementDate ??
      item.HybridNetBankingSettlementDate
    ),
    lumpsumDebitCardSettlement: coerceOptionalSettlementDate(
      item.lumpsumDebitCardSettlementDays ??
      item.nbDebitCardSettlementDays ??
      item.netbanking?.lumpsumDebitCardSettlement ??
      item.lumpsumDebitCardSettlementDate ??
      item.LumpsumDebitCardSettlementDate
    ),
  },
  emandate: {
    bankId: item.emandateBankId ?? '-',
    bankIdCorporate: item.emandateBankIdCorporate ?? undefined,
    netBanking: coerceBoolean(
      item.emandate?.netBanking ??
      item.emdNetBanking ??
      item.EmandateNetBanking ??
      item.EmandateNetbanking
    ),
    debitCard: coerceBoolean(
      item.emandate?.debitCard ?? item.emdDebitCard ?? item.EmandateDebitCard
    ),
    aadhaar: coerceBoolean(
      item.emandate?.aadhaar ?? item.emdAadhaar ?? item.EmandateAadhar
    ),
    pan: coerceBoolean(
      item.emandate?.pan ?? item.emdPan ?? item.EmandatePAN ?? item.EmandatePan
    ),
    netBankingSettlement: coerceOptionalSettlementDate(
      item.emandateNetBankingSettlementDays ??
      item.emdNetBankingSettlementDays ??
      item.emandate?.netBankingSettlement
    ),
    debitCardSettlement: coerceOptionalSettlementDate(
      item.emandateDebitCardSettlementDays ??
      item.emdDebitCardSettlementDays ??
      item.emandate?.debitCardSettlement
    ),
    aadhaarSettlement: coerceOptionalSettlementDate(
      item.emandateAadharSettlementDays ??
      item.emandateAadhaarSettlementDays ??
      item.emdAadhaarSettlementDays ??
      item.emandate?.aadhaarSettlement
    ),
    panSettlement: coerceOptionalSettlementDate(
      item.emandatePANSettlementDays ??
      item.emandatePanSettlementDays ??
      item.emdPanSettlementDays ??
      item.emandate?.panSettlement
    ),
  },
  blacklistedMerchants: (item.blacklistedMerchants || []).map((m: any) => 
    typeof m === 'string' 
      ? { merchantId: m, isActive: true } 
      : { ...m, isActive: m.isActive !== undefined ? coerceBoolean(m.isActive) : true }
  ),
  blacklistReason: item.blacklistReason || '',
  // Per-mode settlement days are only populated when the API was called with a
  // merchantId; otherwise these are all undefined and the UI hides the column.
  settlementDays: {
    lumpsumNetBanking: item.lumpsumNetBankingSettlementDays,
    lumpsumDebitCard: item.lumpsumDebitCardSettlementDays,
    hybridNetBanking: item.hybridNetBankingSettlementDays,
    emandateNetBanking: item.emandateNetBankingSettlementDays,
    emandateDebitCard: item.emandateDebitCardSettlementDays,
    emandateAadhar: item.emandateAadharSettlementDays,
    emandatePAN: item.emandatePANSettlementDays,
  },
});
